import { create } from "zustand";
import { removeSystem as apiRemoveSystem } from "../api/chains";
import type { Chain, Connection, Signature, System } from "../types/chain";
import { useLayoutStore, type XY } from "./layoutStore";

function withSignatureDefaults(systems: System[]): System[] {
  return systems.map((s) => (s.signatures ? s : { ...s, signatures: [] }));
}

function hydratePositions(systems: System[]) {
  const layout = useLayoutStore.getState();
  const updates: Record<string, XY> = {};
  for (const system of systems) {
    const pos = system.position;
    if (pos == null) continue;
    if (layout.pendingWrites.has(system.id)) continue;
    if (layout.isDragging.has(system.id)) continue;
    updates[system.id] = { x: pos.x, y: pos.y };
  }
  if (Object.keys(updates).length > 0) {
    useLayoutStore.setState((s) => ({
      positions: { ...s.positions, ...updates },
    }));
  }
}

interface ChainState {
  chainId: string | null;
  rootSystemId: string | null;
  systems: System[];
  connections: Connection[];
  signaturesUndo: Record<string, Signature[] | null>;
  setChain: (chain: Chain) => void;
  clearChain: () => void;
  updateSystem: (id: string, patch: Partial<System>) => void;
  updateConnection: (id: string, patch: Partial<Connection>) => void;
  addSystem: (system: System) => void;
  addConnection: (connection: Connection) => void;
  removeSystem: (id: string) => Promise<void>;
  replaceSignatures: (systemId: string, signatures: Signature[]) => void;
  captureSignaturesUndo: (systemId: string, prev: Signature[]) => void;
  clearSignaturesUndo: (systemId: string) => void;
}

export const useChainStore = create<ChainState>((set, get) => ({
  chainId: null,
  rootSystemId: null,
  systems: [],
  connections: [],
  signaturesUndo: {},
  setChain: (chain) => {
    const systemIds = new Set(chain.systems.map((s) => s.id));
    useLayoutStore.getState().prune(systemIds);
    hydratePositions(chain.systems);
    set((s) => {
      const carry = s.chainId === chain.id ? s.signaturesUndo : {};
      const nextUndo: Record<string, Signature[] | null> = {};
      for (const [sid, snap] of Object.entries(carry)) {
        if (systemIds.has(sid)) nextUndo[sid] = snap;
      }
      return {
        chainId: chain.id,
        rootSystemId: chain.root_system_id,
        systems: withSignatureDefaults(chain.systems),
        connections: chain.connections,
        signaturesUndo: nextUndo,
      };
    });
  },
  clearChain: () => {
    set({
      chainId: null,
      rootSystemId: null,
      systems: [],
      connections: [],
      signaturesUndo: {},
    });
  },
  updateSystem: (id, patch) =>
    set((s) => ({
      systems: s.systems.map((n) => (n.id === id ? { ...n, ...patch } : n)),
    })),
  updateConnection: (id, patch) =>
    set((s) => ({
      connections: s.connections.map((e) =>
        e.id === id ? { ...e, ...patch } : e,
      ),
    })),
  addSystem: (system) =>
    set((s) => ({
      systems: [
        ...s.systems,
        system.signatures ? system : { ...system, signatures: [] },
      ],
    })),
  addConnection: (connection) =>
    set((s) => ({ connections: [...s.connections, connection] })),
  removeSystem: async (id) => {
    const { chainId, rootSystemId, systems, connections } = get();
    if (!chainId) return;
    await apiRemoveSystem(chainId, id);

    const survivingConnections = connections.filter(
      (c) => c.from !== id && c.to !== id,
    );

    const orphans = new Set<string>();
    if (rootSystemId && rootSystemId !== id) {
      const adjacency = new Map<string, string[]>();
      for (const c of survivingConnections) {
        if (!adjacency.has(c.from)) adjacency.set(c.from, []);
        if (!adjacency.has(c.to)) adjacency.set(c.to, []);
        adjacency.get(c.from)!.push(c.to);
        adjacency.get(c.to)!.push(c.from);
      }
      const reachable = new Set<string>([rootSystemId]);
      const queue: string[] = [rootSystemId];
      while (queue.length > 0) {
        const node = queue.shift()!;
        for (const neighbor of adjacency.get(node) ?? []) {
          if (!reachable.has(neighbor)) {
            reachable.add(neighbor);
            queue.push(neighbor);
          }
        }
      }
      for (const sys of systems) {
        if (sys.id !== id && !reachable.has(sys.id)) {
          orphans.add(sys.id);
        }
      }
    }

    const removed = new Set<string>([id, ...orphans]);
    const nextSystems = systems.filter((sys) => !removed.has(sys.id));
    const nextConnections = survivingConnections.filter(
      (c) => !orphans.has(c.from) && !orphans.has(c.to),
    );
    set((s) => {
      let nextUndo = s.signaturesUndo;
      let cloned = false;
      for (const rid of removed) {
        if (rid in nextUndo) {
          if (!cloned) {
            nextUndo = { ...s.signaturesUndo };
            cloned = true;
          }
          delete nextUndo[rid];
        }
      }
      return {
        systems: nextSystems,
        connections: nextConnections,
        signaturesUndo: nextUndo,
      };
    });

    useLayoutStore.setState((s) => {
      const nextPositions: Record<string, XY> = {};
      for (const [pid, pos] of Object.entries(s.positions)) {
        if (!removed.has(pid)) nextPositions[pid] = pos;
      }
      let nextPending = s.pendingWrites;
      let pendingCloned = false;
      for (const rid of removed) {
        if (s.pendingWrites.has(rid)) {
          if (!pendingCloned) {
            nextPending = new Set(s.pendingWrites);
            pendingCloned = true;
          }
          nextPending.delete(rid);
        }
      }
      let nextDragging = s.isDragging;
      let draggingCloned = false;
      for (const rid of removed) {
        if (s.isDragging.has(rid)) {
          if (!draggingCloned) {
            nextDragging = new Set(s.isDragging);
            draggingCloned = true;
          }
          nextDragging.delete(rid);
        }
      }
      return {
        positions: nextPositions,
        pendingWrites: nextPending,
        isDragging: nextDragging,
      };
    });
  },
  replaceSignatures: (systemId, signatures) =>
    set((s) => ({
      systems: s.systems.map((sys) =>
        sys.id === systemId ? { ...sys, signatures } : sys,
      ),
    })),
  captureSignaturesUndo: (systemId, prev) =>
    set((s) => ({
      signaturesUndo: { ...s.signaturesUndo, [systemId]: prev },
    })),
  clearSignaturesUndo: (systemId) =>
    set((s) => {
      if (!(systemId in s.signaturesUndo)) return s;
      const next = { ...s.signaturesUndo };
      delete next[systemId];
      return { signaturesUndo: next };
    }),
}));
